import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { t } from 'translation'
import history from 'utils/appHistory'

import AuthErrorBox from 'components/Auth/AuthErrorBox'
import LoadingButton from 'components/ui/LoadingButton/LoadingButton'
import GoogleLoginButton from 'components/ui/GoogleLoginButton'

import eyeShowIcon from '../../assets/eye_show.png'
import eyeHideIcon from '../../assets/eye_hide.png'

import { setAuthSocialMessage } from 'store/actions'

const loginSchema = Yup.object().shape({
  email: Yup.string().email('INVALID_EMAIL').required('EMAIL_IS_REQUIRED'),
  password: Yup.string()
    .min(6, 'PASSWORD_IS_TOO_SHORT')
    .max(256, 'PASSWORD_IS_TOO_LONG')
    .required('PASSWORD_IS_REQUIRED'),
})

const AuthLoginForm = ({
  submitFormHandler,
  resetPasswordHandler,
  apiResponseError,
  resetApiResponseError,
  socialMessage,
  setAuthSocialMessage,
  loading,
}) => {
  const isIos = window.cordova && window.device?.platform === 'iOS'
  const isAndroid = window.cordova && window.device?.platform === 'Android'
  const [showPasswords, setShowPasswords] = useState(false)

  useEffect(() => {
    if (
      apiResponseError ===
      'PREAUTHENTICATION_FAILED_WITH_ERROR_NUMBER_OF_DEVICES_EXCEEDED'
    ) {
      resetApiResponseError()
      history.push({
        pathname: '/license',
        error: true,
      })
    }
  }, [apiResponseError])

  return (
    <Formik
      initialValues={{ email: '', password: '' }}
      validationSchema={loginSchema}
      onSubmit={submitFormHandler}
    >
      {({ errors, touched, submitForm, handleChange }) => (
        <Form className="tab_content login_mod active_tab">
          <h2 className="section_title v3_mod center_level_mod color_mod offset_v3_mod">
            {t('auth.LOG_IN_TO_YOUR_ACCOUNT')}
          </h2>
          {socialMessage && (
            <div className="form_success">{t(`auth.${socialMessage}`)}</div>
          )}
          {!socialMessage && (
            <AuthErrorBox
              formFields={['email', 'password']}
              formTouched={touched}
              formErrors={errors}
              apiResponseError={apiResponseError}
              resetApiResponseError={resetApiResponseError}
            />
          )}
          <div className="form_controller login_mod">
            <dl className="form_cell form_cell_v1_mod">
              <dt className="form_cell_title form_v3_mod">
                <label htmlFor="login_email">{t('auth.EMAIL')}</label>
              </dt>
              <dd className="form_field_wrap form_v3_mod">
                <Field
                  className="form_field v4_mod"
                  type="email"
                  name="email"
                  id="login_email"
                  placeholder=""
                  disabled={loading}
                  onChange={(event) => (setAuthSocialMessage(), handleChange(event))}
                />
              </dd>
            </dl>
          </div>
          <div className="form_controller login_mod">
            <dl className="form_cell form_cell_v1_mod">
              <dt className="form_cell_title form_v3_mod">
                <label htmlFor="login_pass">{t('auth.PASSWORD')}</label>
              </dt>
              <dd className="form_field_wrap form_v3_mod">
                <Field
                  className="form_field v4_mod"
                  name="password"
                  type={showPasswords ? 'text' : 'password'}
                  id="login_pass"
                  placeholder="+++++"
                  disabled={loading}
                />
                <img
                  className="form_eye"
                  src={showPasswords ? eyeHideIcon : eyeShowIcon}
                  onClick={() => setShowPasswords(!showPasswords)}
                />
              </dd>
            </dl>
          </div>
          <div className="login_bottom">
            <LoadingButton
              className="btn_base_v1 full_mod"
              id="login_submit"
              isLoading={loading}
              onClick={() => (
                setAuthSocialMessage(), resetApiResponseError(), submitForm()
              )}
              type="submit"
            >
              {t('auth.LOG_IN')}
            </LoadingButton>
          </div>
          {!isIos && !isAndroid && <GoogleLoginButton disabled={loading} />}
          <div className="login_descr">
            <div className="login_descr_text">{t('auth.IN_CASE_YOU_FORGOT')} </div>
            <button
              className="login_descr_link"
              onClick={resetPasswordHandler}
              disabled={loading}
            >
              {t('auth.RESET_MY_PASSWORD')}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

const mapStateToProps = ({ auth: { loading, socialMessage } }) => ({
  loading,
  socialMessage,
})

const willMapDispatchToProps = { setAuthSocialMessage }

export default connect(mapStateToProps, willMapDispatchToProps)(AuthLoginForm)

AuthLoginForm.propTypes = {
  submitFormHandler: PropTypes.func.isRequired,
  resetPasswordHandler: PropTypes.func.isRequired,
  apiResponseError: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  resetApiResponseError: PropTypes.func.isRequired,
  socialMessage: PropTypes.string.isRequired,
  setAuthSocialMessage: PropTypes.func.isRequired,
}
